import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeProvider";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import ModuleRouter from "@/pages/ModuleRouter";
import PublicTripViewer from "@/pages/public/PublicTripViewer";
import TravelDocViewer from "@/pages/public/TravelDocViewer";
import PayLink from "@/pages/public/PayLink";
import PayCurated from "@/pages/public/PayCurated";
import PayExtra from "@/pages/public/PayExtra";
import TripPlanMockup from "@/mockup/TripPlanMockup";
import TripPlanFora from "@/mockup/TripPlanFora";
import ReservaLayoutMockup from "@/mockup/ReservaLayoutMockup";

const AsistenteApp = lazy(() => import("@/asistente/AsistenteApp"));
const B2BApp = lazy(() => import("@/b2b/B2BApp"));
const CashflowApp = lazy(() => import("@/cashflow/CashflowApp"));
const FacturasApp = lazy(() => import("@/facturas/FacturasApp"));
const B2CApp = lazy(() => import("@/b2c/B2CApp"));

function RedirectHome() {
  const { navigation } = useAuth();
  const first = navigation?.[0]?.items?.[0]?.id;
  return <Navigate to={first ? `/app/${first}` : "/app/dashboard"} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/trip/:token" element={<PublicTripViewer />} />
            <Route path="/td/:token" element={<TravelDocViewer />} />
            <Route path="/curated/:token" element={<PublicTripViewer curatedMode />} />
            <Route path="/pay/curated/:token" element={<PayCurated />} />
            <Route path="/pay/extra/:token" element={<PayExtra />} />
            <Route path="/pay/:token" element={<PayLink />} />
            <Route path="/mockup/trip-plan" element={<TripPlanMockup variant="proposal" />} />
            <Route path="/mockup/trip-plan-fora" element={<TripPlanFora />} />
            <Route path="/mockup/travel-documents" element={<TripPlanMockup variant="documents" />} />
            <Route path="/mockup/reserva-layout" element={<ReservaLayoutMockup />} />
            <Route
              path="/asistente/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Cargando…</div>}>
                    <AsistenteApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/comercial-b2b/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Cargando…</div>}>
                    <B2BApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cashflow-app/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Cargando…</div>}>
                    <CashflowApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/facturas-app/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Cargando…</div>}>
                    <FacturasApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/b2c-app/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Cargando…</div>}>
                    <B2CApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route index element={<RedirectHome />} />
              <Route path=":moduleId" element={<ModuleRouter />} />
            </Route>
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}
